// src/pages/admin/IntegrationQueryList.tsx

import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog"; // Modal için
import { InputSwitch } from "primereact/inputswitch";
import { toast } from "react-toastify";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { IconButton, TextButton } from "../../components/ButtonComponents";
import { Edit, Trash2 } from "lucide-react";

import { useDynamicScrollHeight } from "../../hooks/useDynamicScrollHeight";
import { useTableHeader } from "../../hooks/useTableHeader";
import { Filter } from "../../types/filter";
import { IntegrationApplication, IntegrationQueryTemplate } from "../../types/integration";
import { useIntegrationStore } from "../../store/integrationStore";
import { QueryParams } from "../../types"; 
import { IntegrationQueryForm } from "./IntegrationQueryForm";

// 💡 Query Form'u import edin 


// Component Prop Tipleri
interface QueryListProps {
    isOpen: boolean;
    onClose: () => void;
    application: IntegrationApplication; // Hangi uygulamaya ait olduğunu bilmek için
}

export const IntegrationQueryList: React.FC<QueryListProps> = ({ isOpen, onClose, application }) => {
    
    // Store'dan sadece Query'lere ait metotları ve state'i çekiyoruz
    const {
        queries,
        queryTotal,
        queryLoading,
        queryParams, // App için kullanılan params'lar burada da kullanılacak
        setQueryParams,
        
        fetchQueriesDynamic, 
        createQuery,       
        updateQuery,       
        deleteQuery,       
    } = useIntegrationStore();

    // DATATABLE STATE
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(queryParams.pageSize);
    const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
    const [queryToEdit, setQueryToEdit] = useState<IntegrationQueryTemplate | null>(null);
    const scrollHeight = useDynamicScrollHeight(260);
    const { confirm, modal: confirmModal } = useConfirmModal();
    
    // Uygulama ID'sine göre filtre (Sabit filtre)
    const fixedFilter: Filter = { 
        field: 'applicationId', 
        operator: 'eq', 
        value: application.id 
    };

    // --- Modal ve CRUD Metotları ---
    
    const handleNewQuery = () => {
        setQueryToEdit(null);
        setIsQueryModalOpen(true);
    };

    const handleEditQuery = (query: IntegrationQueryTemplate) => {
        setQueryToEdit(query);
        setIsQueryModalOpen(true);
    };
    
    const handleCloseQueryModal = () => {
        setIsQueryModalOpen(false);
        setQueryToEdit(null);
    };

    const handleDeleteQuery = async (query: IntegrationQueryTemplate) => {
        const ok = await confirm(`${query.name} sorgu şablonunu silmek istiyor musun?`);

        if (ok) {
            try {
                await deleteQuery(query.id); 
                toast.success("Sorgu şablonu silindi");
                fetchCurrentQueries();
            } catch (error) {
                toast.error("Silme işlemi başarısız oldu.");
            }
        }
    };
    
    const handleSaveQuery = async (queryData: any) => {
        try {
            if (queryToEdit) {
                // Güncelleme
                await updateQuery(queryToEdit.id, queryData); 
                toast.success(`Sorgu Güncellendi: ${queryData.name}`);
            } else {
                // Yeni Oluşturma
                // application.id'yi body'ye ekle
                await createQuery({ ...queryData, applicationId: application.id });
                toast.success(`Yeni Sorgu Eklendi: ${queryData.name}`);
            }
            // Başarılı işlem sonrası formu kapat
            handleCloseQueryModal();
            fetchCurrentQueries();
        } catch (error) {
            toast.error("İşlem sırasında hata oluştu");
        }
    };

    // --- DataTable Header ve Filtreleme ---
    const { header, dynamicFilter } = useTableHeader({
        title: `${application.name} Sorgu Şablonları`,
        placeholder: "Sorgu Adı/Endpoint Ara",
        searchFields: [
            { field: "name", operator: "contains" },
            { field: "targetEndpoint", operator: "contains" }, 
        ],
        debounceMs: 350,
        actions: (
            <TextButton onClick={handleNewQuery} className="btn-new">
                Yeni Sorgu Ekle
            </TextButton>
        ),
    });

    // Pagination
    const handlePage = (event: any) => {
        const newParams = { 
            ...queryParams, 
            pageIndex: event.page + 1, 
            pageSize: event.rows 
        };
        setFirst(event.first);
        setRows(event.rows);
        setQueryParams(newParams);
        fetchQueriesDynamic(newParams); 
    };

    const fetchCurrentQueries = () => {
        const pageIndex = Math.floor(first / (rows ?? queryParams.pageSize ?? 10)) + 1;
        const finalFilter = dynamicFilter 
            ? { logic: "and" as const, filters: [dynamicFilter as Filter, fixedFilter] }
            : fixedFilter;
            
        const query: QueryParams = {
            pageIndex,
            pageSize: rows,
            filter: finalFilter, 
        }
        
        fetchQueriesDynamic(query);
    };

    // --- Fetch Query Data ---
    useEffect(() => {
        fetchCurrentQueries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, dynamicFilter, application.id, first]); // application.id veya first değiştiğinde yeniden yükle

    const handleToggleActive = async (query: IntegrationQueryTemplate, newStatus: boolean) => {
        try {
            await updateQuery(query.id, { isActive: newStatus });
            toast.success(`${query.name} durumu güncellendi.`);
            fetchCurrentQueries();
        } catch (error) {
            toast.error("Durum güncellenirken hata oluştu.");
        }
    };

    // DataTable body templates
    const activeBodyTemplate = (query: IntegrationQueryTemplate) => (
        <InputSwitch 
            checked={query.isActive} 
            onChange={(e) => handleToggleActive(query, e.value)} 
        />
    );

    const actionBodyTemplate = (query: IntegrationQueryTemplate) => (
        <div className="flex gap-2">
            <IconButton onClick={() => handleEditQuery(query)} icon={Edit} className="btn-sm text-blue-600 hover:text-blue-800"  />
            <IconButton onClick={() => handleDeleteQuery(query)} icon={Trash2} className="btn-sm text-red-500 hover:text-red-700"  />
        </div>
    );

    return (
        <Dialog 
            header={`Sorgu Şablonları Yönetimi: ${application.name}`}
            visible={isOpen}
            onHide={onClose}
            className="w-11/12 md:w-4/5 lg:w-3/5 xl:w-2/3"
            modal
        maximizable
        maximized
      resizable={false}
        >
            {confirmModal}

            <DataTable
                value={queries} 
                header={header}
                paginator
                lazy
                emptyMessage="Sorgu şablonu bulunamadı."
                first={first}
                rows={rows}
                totalRecords={queryTotal}
                onPage={handlePage}
                loading={queryLoading}
                rowsPerPageOptions={[5, 10, 20, 50]}
              
                filterDisplay="menu"
                scrollable
                scrollHeight={scrollHeight}
            >
                <Column field="id" header="ID" style={{ width: "4rem" }}/>
                <Column field="name" header="Sorgu Adı" style={{ width: "10rem" }}/>
                <Column field="targetEndpoint" header="Hedef Endpoint" style={{ width: "15rem" }}/>
              <Column
  field="templateSQL"
  header="SQL Şablonu"
  body={(row) => (
    <span
      className="truncate block max-w-[520px]"
      title={row.templateSQL} // hover’da tamamını gösterir
    >
      {row.templateSQL}
    </span>
  )}
/>

                <Column field="isActive" header="Aktif" dataType="boolean" body={activeBodyTemplate} style={{ width: "6rem" }}/>
                <Column header="İşlemler" body={actionBodyTemplate} align="center" style={{ width: "8rem" }} />
            </DataTable>

            {/* 👇 BURAYA EKLENEN KISIM: Query Ekleme/Düzenleme Formu Modalı */}
            <IntegrationQueryForm 
                visible={isQueryModalOpen} 
                onHide={handleCloseQueryModal} 
                onSave={handleSaveQuery} 
                initialData={queryToEdit} 
            />
            
        </Dialog>
    );
};